/*
 * wishlist.js
 * ------------------------------------------------------------
 * Wishlist UI. Storage and syncing live in account-sync.js
 * (window.DealSync); this file only handles the page:
 *  - star buttons:    [data-wishlist-toggle] (snippets/wishlist-button.liquid)
 *  - header counter:  [data-wishlist-bubble] > [data-wishlist-count]
 *  - wishlist page:   [data-wishlist-page]   (sections/main-wishlist.liquid)
 * Cards on the wishlist page come from templates/product.wishlist-card.liquid
 * (the same card-product snippet as the collection grid).
 * ------------------------------------------------------------
 */
(function () {
  'use strict';

  if (!window.DealSync) return;

  var store = window.DealSync.wishlist;
  var strings = window.DealSync.strings;
  // The wishlist is for logged-in customers. Guests' stars stay empty and
  // a click opens a small login popup (see the click handler).
  var isLoggedIn = window.DealSync.isLoggedIn;

  /* ---------------- star buttons ---------------- */

  function paintButton(btn) {
    var active = isLoggedIn && store.has(btn.dataset.productHandle);
    btn.classList.toggle('is-active', active);
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    var label = active ? strings.remove : strings.add;
    btn.setAttribute('aria-label', label);
    var text = btn.querySelector('[data-wishlist-label]');
    if (text) {
      // Visible label (product page): "שמירה ברשימה שלי" / saved "שמור ברשימה שלך".
      // No title tooltip on these - it would sit on top of the visible text.
      text.textContent = active ? strings.added : strings.add;
      btn.removeAttribute('title');
    } else {
      btn.setAttribute('title', label);
    }
  }

  function paintAll(root) {
    (root || document).querySelectorAll('[data-wishlist-toggle]').forEach(paintButton);
    var count = isLoggedIn ? store.count() : 0;
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      el.textContent = count;
    });
    document.querySelectorAll('[data-wishlist-bubble]').forEach(function (el) {
      el.classList.toggle('is-visible', count > 0);
    });
  }

  document.addEventListener('click', function (evt) {
    if (isLoggedIn) return;
    var link = evt.target.closest('[data-wishlist-login]');
    if (!link) return;
    if (window.Shopify && Shopify.designMode) return;
    if (typeof HTMLDialogElement !== 'function') return;
    evt.preventDefault();
    evt.stopPropagation();
    openLoginDialog(null, link, window.DealSync.wishlistUrl || '/');
  }, true);

  document.addEventListener('click', function (evt) {
    var btn = evt.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    // Stars sit inside product cards that are links - don't navigate.
    evt.preventDefault();
    evt.stopPropagation();

    if (!isLoggedIn) {
      if (window.Shopify && Shopify.designMode) return;
      openLoginDialog({
        handle: btn.dataset.productHandle,
        product_id: btn.dataset.productId,
        variant_id: btn.dataset.variantId,
      }, btn);
      return;
    }

    var added = store.toggle({
      handle: btn.dataset.productHandle,
      product_id: btn.dataset.productId,
      variant_id: btn.dataset.variantId,
    });
    btn.classList.remove('is-animating');
    void btn.offsetWidth;
    btn.classList.add('is-animating');
    toast(added ? strings.added_toast : strings.removed_toast, added);
  }, true);

  document.addEventListener('wishlist:changed', function () {
    paintAll();
  });

  // Cards are added after load by infinite scroll, recommendations, quick
  // view, etc. Paint any new stars as they appear.
  if ('MutationObserver' in window) {
    var scheduled = false;
    new MutationObserver(function (mutations) {
      if (scheduled) return;
      var relevant = mutations.some(function (m) { return m.addedNodes.length > 0; });
      if (!relevant) return;
      scheduled = true;
      requestAnimationFrame(function () {
        scheduled = false;
        document.querySelectorAll('[data-wishlist-toggle]:not([aria-pressed])').forEach(paintButton);
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  /* ---------------- guest login popup ---------------- */

  var loginDialog;
  var loginCta;
  var pendingItem = null;
  var pendingReturn = null;

  // The product is kept in this browser only when the guest goes on to log
  // in; account-sync.js moves it into the account on the first page view
  // after login. Closing the popup saves nothing.
  function currentPath() {
    return window.location.pathname + window.location.search;
  }

  function goToLogin(item, returnTo) {
    if (item) store.add(item);
    window.location.href = window.DealSync.loginUrl(returnTo || currentPath());
  }

  function buildLoginDialog(starButton) {
    var dialog = document.createElement('dialog');
    dialog.className = 'favorites-login-dialog';
    dialog.setAttribute('aria-labelledby', 'FavoritesLoginTitle');

    var body = document.createElement('div');

    var close = document.createElement('button');
    close.type = 'button';
    close.className = 'favorites-login-dialog__close';
    close.setAttribute('aria-label', strings.close || '');
    close.textContent = '×';
    close.addEventListener('click', function () { dialog.close(); });

    var icon = starButton.querySelector('svg.icon-star') || document.querySelector('svg.icon-star');
    if (icon) body.appendChild(icon.cloneNode(true));

    var title = document.createElement('p');
    title.id = 'FavoritesLoginTitle';
    title.className = 'favorites-login-dialog__title';
    title.textContent = strings.login_title || '';

    var text = document.createElement('p');
    text.className = 'favorites-login-dialog__text';
    text.textContent = strings.login_text || '';

    var cta = document.createElement('a');
    cta.className = 'btn favorites-login-dialog__cta';
    cta.textContent = strings.login_cta || '';
    cta.href = window.DealSync.loginUrl(currentPath());
    cta.addEventListener('click', function (evt) {
      evt.preventDefault();
      goToLogin(pendingItem, pendingReturn);
    });
    loginCta = cta;

    body.appendChild(title);
    body.appendChild(text);
    body.appendChild(cta);
    dialog.appendChild(close);
    dialog.appendChild(body);

    // A click on the dimmed backdrop lands on the <dialog> itself.
    dialog.addEventListener('click', function (evt) {
      if (evt.target === dialog) dialog.close();
    });
    dialog.addEventListener('close', function () {
      pendingItem = null;
      pendingReturn = null;
    });

    document.body.appendChild(dialog);
    return dialog;
  }

  function openLoginDialog(item, starButton, returnTo) {
    // Very old browsers without <dialog>: straight to login, as before.
    if (typeof HTMLDialogElement !== 'function') {
      goToLogin(item, returnTo);
      return;
    }
    if (!loginDialog) loginDialog = buildLoginDialog(starButton);
    pendingItem = item;
    pendingReturn = returnTo || null;
    loginCta.href = window.DealSync.loginUrl(pendingReturn || currentPath());
    if (!loginDialog.open) loginDialog.showModal();
  }

  /* ---------------- toast ---------------- */

  var toastEl;
  var toastTimer;

  function toast(message, withLink) {
    if (!message) return;
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'wishlist-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }
    toastEl.innerHTML = '';
    var text = document.createElement('span');
    text.textContent = message;
    toastEl.appendChild(text);
    if (withLink && window.DealSync.wishlistUrl) {
      var link = document.createElement('a');
      link.href = window.DealSync.wishlistUrl;
      link.textContent = strings.view;
      toastEl.appendChild(link);
    }
    toastEl.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      toastEl.classList.remove('is-visible');
    }, 2600);
  }

  /* ---------------- wishlist page ---------------- */

  function initWishlistPage(page) {
    var grid = page.querySelector('[data-wishlist-grid]');
    var empty = page.querySelector('[data-wishlist-empty]');
    var loading = page.querySelector('[data-wishlist-loading]');
    var actions = page.querySelector('[data-wishlist-actions]');
    var clearButton = page.querySelector('[data-wishlist-clear]');
    var rendered = {}; // handle -> <li>
    var loadingNow = {};
    var failed = {};

    function fetchCard(handle) {
      var url = (window.Shopify && Shopify.routes && Shopify.routes.root ? Shopify.routes.root : '/') +
        'products/' + encodeURIComponent(handle) + '?view=wishlist-card';
      return fetch(url, { credentials: 'same-origin' }).then(function (res) {
        if (res.status === 404) {
          failed[handle] = 'gone';
          return null;
        }
        if (!res.ok) throw new Error(res.status);
        return res.text();
      });
    }

    function toItem(html) {
      var tpl = document.createElement('template');
      tpl.innerHTML = html.trim();
      return tpl.content.querySelector('li');
    }

    // One skeleton card per product being fetched, up to 8.
    function showSkeleton(count) {
      var cards = loading.children;
      var wanted = Math.max(1, Math.min(8, count));
      if (!cards.length) return;
      while (cards.length < wanted) loading.appendChild(cards[0].cloneNode(true));
      while (cards.length > wanted) loading.removeChild(cards[cards.length - 1]);
      loading.hidden = false;
      page.setAttribute('aria-busy', 'true');
    }

    function hideSkeleton() {
      loading.hidden = true;
      page.setAttribute('aria-busy', 'false');
    }

    // Fade new cards in one after another. The class comes off when the
    // animation ends, so moving a card later (re-sorting) doesn't replay it.
    function animateIn(items) {
      items.forEach(function (li, index) {
        li.style.setProperty('--wishlist-i', Math.min(index, 8));
        li.classList.add('wishlist-card-enter');
        li.addEventListener('animationend', function done(evt) {
          if (evt.target !== li) return;
          li.classList.remove('wishlist-card-enter');
          li.style.removeProperty('--wishlist-i');
          li.removeEventListener('animationend', done);
        });
      });
    }

    function render() {
      var items = store.items();
      var wanted = {};
      items.forEach(function (i) { wanted[i.handle] = true; });

      Object.keys(rendered).forEach(function (handle) {
        if (!wanted[handle]) {
          rendered[handle].remove();
          delete rendered[handle];
        }
      });

      var missing = items.filter(function (i) {
        return !rendered[i.handle] && !failed[i.handle] && !loadingNow[i.handle];
      });
      missing.forEach(function (i) { loadingNow[i.handle] = true; });
      empty.hidden = items.length > 0;
      if (actions) actions.hidden = items.length === 0;
      if (!missing.length) {
        hideSkeleton();
        grid.hidden = items.length === 0;
        return;
      }

      showSkeleton(missing.length);
      var added = [];
      var queue = missing.slice();
      var workers = [];
      // Up to 6 requests at a time.
      for (var w = 0; w < Math.min(6, queue.length); w++) {
        workers.push((function next() {
          var item = queue.shift();
          if (!item) return Promise.resolve();
          return fetchCard(item.handle)
            .then(function (html) {
              if (!html || !store.has(item.handle)) return;
              var li = toItem(html);
              if (li) {
                rendered[item.handle] = li;
                added.push(li);
              }
            })
            .catch(function () {})
            .then(function () {
              delete loadingNow[item.handle];
              return next();
            });
        })());
      }

      Promise.all(workers).then(function () {
        // Products that were deleted from the store are dropped from the list.
        Object.keys(failed).forEach(function (handle) {
          if (failed[handle] === 'gone' && store.has(handle)) store.remove(handle);
        });
        // Keep the wishlist order (newest first).
        var ordered = [];
        store.items().forEach(function (i) {
          if (!rendered[i.handle]) return;
          grid.appendChild(rendered[i.handle]);
          if (added.indexOf(rendered[i.handle]) !== -1) ordered.push(rendered[i.handle]);
        });
        animateIn(ordered);
        hideSkeleton();
        grid.hidden = grid.children.length === 0;
        empty.hidden = store.count() > 0;
        if (actions) actions.hidden = store.count() === 0;
        paintAll(grid);
        // Bind the theme's quick add ("הוספה לסל") on the new cards only:
        // theme.QuickAdd adds a click listener to every button in the
        // container it gets, so passing the whole grid again would make the
        // cards from an earlier batch add to cart twice.
        if (window.theme && typeof theme.QuickAdd === 'function') {
          added.forEach(function (li) { new theme.QuickAdd(li); });
        }
      });
    }

    if (clearButton) {
      clearButton.addEventListener('click', function () {
        if (!window.confirm(clearButton.dataset.confirm)) return;
        store.clear();
      });
    }

    render();
    document.addEventListener('wishlist:changed', render);
  }

  /* ---------------- boot ---------------- */

  function init() {
    // Passing through the one-time account sign-in redirect - wait, and
    // only render if the redirect did not happen after all.
    if (window.DealSync.redirecting) {
      document.addEventListener('dealsync:ready', init, { once: true });
      return;
    }
    paintAll();
    document.querySelectorAll('[data-wishlist-page]').forEach(initWishlistPage);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
